import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import {
  createChart,
  LineSeries,
  ColorType,
  type IChartApi,
  type ISeriesApi,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { CandleData } from '../types/gateway';
import { useTerminalStore } from '../store/useTerminalStore';
import { usePanelState } from '../hooks/usePanelState';
import { PANEL_SOURCE } from '../lib/panelSources';

interface EquityCurvePanelProps {
  candles: CandleData[];
  className?: string;
}

const GREEN = '#01E599';
const RED = '#FF3366';
const GRID_COLOR = '#1E293B';
const TEXT_COLOR = '#7A8C9E';

export function EquityCurvePanel({ candles, className = '' }: EquityCurvePanelProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const initializedRef = useRef(false);

  const equityHistory = useTerminalStore((s) => s.equityHistory);

  // DDD #180 S1.2 — tri-state for equity panel.
  // 'equity' source is redis; if redis is down the real equity fetch fails open
  // (returns 200-[]) which otherwise looks like a benign live-empty. The
  // upstreamHealth check in usePanelState escalates that to 'degraded'.
  const equityState = usePanelState('equity', PANEL_SOURCE.equity);

  const sortedEquityHistory = useMemo(
    () => [...equityHistory].sort((a, b) => a.timestamp_ms - b.timestamp_ms),
    [equityHistory],
  );

  const isRealData = sortedEquityHistory.length > 0;

  const equityData = useMemo(() => {
    // Real portfolio equity from gateway — use when available (S8 T2).
    //
    // Codex PR #102 re-review (BTC-extrapolation P2 · 2026-05-14): the prior
    // "tail continuation" pattern extended the chart with selected-symbol candle
    // deltas, but `candles` is the user-selected symbol bar stream — BTC moves
    // would shift the portfolio equity display even when the portfolio holds
    // ETH/SOL/cash (operator-visible misleading display). Authoritative refresh
    // now comes from the `useEquityPolling` hook (60s setInterval re-fetch of
    // /api/portfolio/equity · wired in App.tsx · Codex PR #103 review fix).
    if (sortedEquityHistory.length > 0) {
      return sortedEquityHistory.map((r) => ({
        time: (r.timestamp_ms / 1000) as UTCTimestamp,
        value: r.equity,
      }));
    }
    // Bar-price-derived fallback — cleaner than blank chart when gateway returns 503
    if (candles.length === 0) return [];
    const sorted = [...candles].sort((a, b) => a.time - b.time);
    let cumulative = 0;
    return sorted.map((c) => {
      cumulative += c.close - c.open;
      return { time: c.time as UTCTimestamp, value: cumulative };
    });
  }, [sortedEquityHistory, candles]);

  const currentEquity = equityData.length > 0 ? equityData[equityData.length - 1].value : 0;

  // Signed PnL is the live delta indicator in real-data mode (absolute equity is always
  // positive so cannot drive sign/color). In fallback mode, currentEquity itself is a
  // signed cumulative delta from bar opens/closes.
  const pnlTotal = isRealData
    ? sortedEquityHistory[sortedEquityHistory.length - 1].pnl_realized +
      sortedEquityHistory[sortedEquityHistory.length - 1].pnl_unrealized
    : 0;

  const signValue = isRealData ? pnlTotal : currentEquity;
  const equityColor = signValue >= 0 ? GREEN : RED;

  // Show degraded badge when equity source is down AND we are in bar-derived proxy mode.
  // If isRealData is true the real equity is showing — no badge needed regardless of state.
  const showDegradedBadge = !isRealData && equityState === 'degraded';

  const initChart = useCallback(() => {
    const el = containerRef.current;
    if (!el || initializedRef.current) return;
    initializedRef.current = true;

    const chart = createChart(el, {
      layout: { background: { type: ColorType.Solid, color: 'transparent' }, textColor: TEXT_COLOR },
      grid: { vertLines: { color: GRID_COLOR }, horzLines: { color: GRID_COLOR } },
      rightPriceScale: { borderColor: GRID_COLOR },
      timeScale: { borderColor: GRID_COLOR, timeVisible: true, secondsVisible: false },
      width: el.clientWidth,
      height: el.clientHeight,
    });

    const series = chart.addSeries(LineSeries, {
      color: equityColor,
      lineWidth: 2,
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
    });

    chartRef.current = chart;
    seriesRef.current = series;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry && chartRef.current) {
        const { width, height } = entry.contentRect;
        chartRef.current.applyOptions({ width, height });
      }
    });
    observer.observe(el);
    resizeObserverRef.current = observer;
  }, [equityColor]);

  useEffect(() => {
    initChart();
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
      seriesRef.current = null;
      initializedRef.current = false;
    };
  }, [initChart]);

  useEffect(() => {
    if (seriesRef.current && equityData.length > 0) {
      seriesRef.current.setData(equityData);
      chartRef.current?.timeScale().fitContent();
    }
  }, [equityData]);

  return (
    <div
      className={`hud-corner bg-card rounded border border-border flex flex-col animate-fade-up ${className}`}
      data-testid="equity-curve-panel"
    >
      <div className="drag-handle h-6 flex items-center px-2 cursor-grab active:cursor-grabbing border-b border-border-subtle shrink-0">
        <span className="panel-header">{t('equity.header')}</span>
        {isRealData ? (
          <span className="ml-auto flex items-center gap-2">
            <span className="text-[10px] font-mono font-medium" data-testid="equity-absolute">
              ${currentEquity.toFixed(2)}
            </span>
            <span
              className={`text-[10px] font-mono font-medium ${pnlTotal >= 0 ? 'text-success' : 'text-danger'}`}
              data-testid="equity-pnl-delta"
            >
              {pnlTotal >= 0 ? '+' : ''}{pnlTotal.toFixed(2)}
            </span>
          </span>
        ) : (
          <span className="ml-auto flex items-center gap-2">
            <span
              className={`text-[10px] font-mono font-medium ${currentEquity >= 0 ? 'text-success' : 'text-danger'}`}
              data-testid="equity-fallback-delta"
            >
              {currentEquity >= 0 ? '+' : ''}{currentEquity.toFixed(2)}
            </span>
            {showDegradedBadge && (
              <span
                role="alert"
                data-testid="panel-equity-degraded"
                className="flex items-center gap-1 text-[9px] font-mono text-red-400 border border-red-800/50 bg-red-950/30 rounded-sm px-1.5 py-0.5"
              >
                <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse shrink-0" aria-hidden="true" />
                {t('equity.proxy_badge')}
              </span>
            )}
          </span>
        )}
      </div>
      {/* Chart is ALWAYS rendered — overlay badge is in header only, never replaces canvas */}
      <div
        ref={containerRef}
        className="flex-1 w-full min-h-0"
        role="img"
        aria-label="Equity curve chart"
        data-testid="equity-curve-chart"
      />
    </div>
  );
}
